/**
 * Datenzugriff für auditierte Schreibaktionen im Support-Modus.
 *
 * Phase 2 des Support-Modus erlaubt einem Plattform-Admin, eine offene
 * Support-Session in eine schmale Schreibaktion zu übersetzen — z. B.
 * eine fehlerhafte Patienten-E-Mail oder einen falschen Rechnungs-
 * Status zu korrigieren. Die Validierung läuft komplett serverseitig
 * (RPCs `support_update_patient`, `support_update_invoice_status`); das
 * Frontend reicht nur Eingaben durch und übersetzt Server-Fehler in
 * verständliche Texte.
 *
 * Bewusst KEIN direkter `supabase.from(...).update(...)`: das wäre im
 * Support-Modus durch den Write-Guard (siehe `supportModeFlag.ts`)
 * blockiert. RPCs werden nicht geguarded — die Whitelist-Validierung
 * passiert in der DB.
 */

import { supabase } from '@/integrations/supabase/client';

export type PatientPatch = {
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
};

/**
 * Korrigiert die Stammdaten eines Patienten in der Kunden-Org, in die
 * der Plattform-Admin gerade über Support-Modus eingeloggt ist. Nur
 * email/phone/notes sind in der DB-Whitelist; alles andere bricht
 * serverseitig mit einer expliziten Exception ab.
 *
 * @param patientId  Ziel-Patient. Muss zur Ziel-Org der aktiven Session
 *                   gehören (wird serverseitig geprüft).
 * @param fields     Felder, die geändert werden sollen. NULL/undefined
 *                   = unverändert. Mindestens ein Feld muss gesetzt sein.
 * @param reason     Pflichtfeld (DSGVO Art. 30) — wird in `audit_logs`
 *                   und `support_actions` gespeichert.
 * @returns          Die `id` des `support_actions`-Eintrags, falls der
 *                   Caller die Aktion später referenzieren möchte.
 */
export async function supportUpdatePatient(
  patientId: string,
  fields: PatientPatch,
  reason: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('support_update_patient', {
    p_patient_id: patientId,
    p_email: fields.email ?? null,
    p_phone: fields.phone ?? null,
    p_notes: fields.notes ?? null,
    p_reason: reason,
  });
  if (error) throw error;
  return data as unknown as string;
}

/**
 * Setzt den Status einer Rechnung. Erlaubte Werte sind serverseitig auf
 * `draft | open | paid | overdue | cancelled` beschränkt (Whitelist in
 * der RPC), damit ein versehentlicher Klick keinen unbekannten Status
 * einführt, den die App-Logik nicht kennt.
 */
export async function supportUpdateInvoiceStatus(
  invoiceId: string,
  newStatus: 'draft' | 'open' | 'paid' | 'overdue' | 'cancelled',
  reason: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('support_update_invoice_status', {
    p_invoice_id: invoiceId,
    p_new_status: newStatus,
    p_reason: reason,
  });
  if (error) throw error;
  return data as unknown as string;
}

/**
 * Stellt eine soft-gelöschte Patientenakte wieder her. Schlägt mit
 * einer expliziten Exception fehl, wenn der Patient gar nicht gelöscht
 * ist — bewusste No-Op-Prävention, damit das Audit-Log nur Einträge mit
 * tatsächlicher Zustandsänderung enthält.
 */
export async function supportUndeletePatient(
  patientId: string,
  reason: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('support_undelete_patient', {
    p_patient_id: patientId,
    p_reason: reason,
  });
  if (error) throw error;
  return data as unknown as string;
}

/**
 * Setzt den Status eines Termins. Erlaubte Werte: `scheduled | cancelled
 * | no_show | completed` (Whitelist in der RPC, orientiert sich am
 * Quick-Actions-Filter im Therapeuten-Dashboard).
 */
export async function supportUpdateAppointmentStatus(
  appointmentId: string,
  newStatus: 'scheduled' | 'cancelled' | 'no_show' | 'completed',
  reason: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('support_update_appointment_status', {
    p_appointment_id: appointmentId,
    p_new_status: newStatus,
    p_reason: reason,
  });
  if (error) throw error;
  return data as unknown as string;
}
